/**
 * AP-39 12.3 — Block **„Eingänge"** an einem Ziel (Preisanfrage, Bestellung, Lieferschein,
 * Geschäftspartner): die Dokumente aus dem Posteingang, die dort hängen — Öffnen (Vorschau),
 * Zuordnung lösen. Ziehen auf den Block legt ab und ordnet sofort zu (5.1).
 *
 * AP-39 H (`INBOX.AI`): An Preisanfrage und Bestellung (`mitWerten`) liest die KI die Zeilen als Vorschlag —
 * der Block zeigt „liest …", bietet „Mit KI lesen" und meldet `onGelesen`, wenn ein Lauf fertig ist.
 *
 * Geteilt von Belegwesen und Geschäftspartnern; liegt darum in `components/`. Ohne gebuchten
 * Posteingang oder ohne `inbox.item.read` erscheint nichts.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, Eye, FolderInput, Inbox, Loader2, Mail, Sparkles, Unlink, Upload, X } from 'lucide-react'
import { erfuelltRecht, type InboxKind } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { laufFehlerText } from '@/lib/lauf-fehler'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { downloadFile } from '@/lib/download'
import { fmtDateTime } from '@/lib/format-date'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { ablageflaeche, darfPosteingang, usePosteingang } from './posteingang'
import { ART_TEXT } from './texte'

interface Eingang {
  assignmentId: string
  itemId: string
  fileName: string
  mimeType: string | null
  kind: InboxKind | null
  status: string
  isMailText: boolean
  receivedAt: string
  senderName: string | null
  senderAddress: string | null
  subject: string | null
  aiStatus: 'RUNNING' | 'DONE' | 'FAILED' | null
  aiError: string | null
  aiReadAt: string | null
  excludeFromAi: boolean
}

const ANZEIGBAR = /^(application\/pdf|image\/(png|jpeg|webp))$/
const inhalt = (id: string) => api.get<Blob>(`/inbox/items/${id}/content`, { responseType: 'blob' }).then((r) => r.data)

export function Eingaenge({
  targetKind,
  targetId,
  onGeaendert,
  mitWerten,
  onGelesen,
}: {
  targetKind: string
  targetId: string
  onGeaendert?: () => void
  /** An diesem Ziel liest die KI Werte (Preisanfrage, Bestellung) — nur mit `INBOX.AI` */
  mitWerten?: boolean
  /** Ein Lauf der KI ist fertig: Die Vorschläge am Ziel sind neu */
  onGelesen?: () => void
}) {
  const { user, hasModule } = useAuthStore()
  const mitKi = !!mitWerten && hasModule('INBOX.AI')
  const sichtbar = darfPosteingang()
  const darf = sichtbar && erfuelltRecht('inbox.item.manage', user?.permissions)
  const zaehlerNeu = usePosteingang((s) => s.zaehlerNeu)
  const [zeilen, setZeilen] = useState<Eingang[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [ziehen, setZiehen] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [vorschau, setVorschau] = useState<Eingang | null>(null)
  const datei = useRef<HTMLInputElement>(null)

  const laden = useCallback(async () => {
    try {
      const r = await api.get<{ data: Eingang[] }>('/inbox/assignments', { params: { targetKind, targetId } })
      setZeilen(r.data.data)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Eingänge ließen sich nicht laden.'))
    }
  }, [targetKind, targetId])
  useEffect(() => {
    if (sichtbar) void laden()
  }, [sichtbar, laden])

  // Solange die KI an einem Eingang liest, alle zwei Sekunden nachsehen; ist sie fertig, sind die Vorschläge am Ziel neu.
  const liest = (zeilen ?? []).some((z) => z.aiStatus === 'RUNNING')
  const las = useRef(false)
  useEffect(() => {
    if (las.current && !liest) onGelesen?.()
    las.current = liest
    if (!liest) return
    const t = setInterval(() => void laden(), 2000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liest, laden])

  if (!sichtbar) return null

  const ablegen = async (dateien: File[]) => {
    setFehler(null)
    setHinweis(null)
    setLaeuft(true)
    const form = new FormData()
    form.append('targetKind', targetKind)
    form.append('targetId', targetId)
    for (const d of dateien) form.append('files', d, d.name)
    try {
      const r = await api.post<{ data: { status: string; fileName: string }[]; meta: { assignErrors?: { itemId: string; code: string }[] } }>('/inbox/items', form)
      const abgelehnt = r.data.data.filter((x) => x.status === 'FAILED').map((x) => x.fileName)
      const offen = r.data.meta.assignErrors?.length ?? 0
      setHinweis(
        [abgelehnt.length ? `nicht angenommen: ${abgelehnt.join(', ')}` : '', offen ? `${offen} nicht zugeordnet — liegt im Posteingang` : ''].filter(Boolean).join(' · ') || null,
      )
      await laden()
      void zaehlerNeu()
      onGeaendert?.()
    } catch (e) {
      setFehler(apiErrorText(e, 'Ablegen hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
      if (datei.current) datei.current.value = ''
    }
  }

  const loesen = async (z: Eingang) => {
    if (!(await bestaetige(`„${z.fileName}" von hier lösen? Das Dokument liegt danach wieder offen im Posteingang.`, { ok: 'Lösen' }))) return
    try {
      await api.delete(`/inbox/items/${z.itemId}/assignments/${z.assignmentId}`)
      await laden()
      void zaehlerNeu()
      onGeaendert?.()
    } catch (e) {
      setFehler(apiErrorText(e, 'Lösen hat nicht geklappt.'))
    }
  }

  const mitKiLesen = async (z: Eingang) => {
    setFehler(null)
    try {
      await api.post(`/inbox/items/${z.itemId}/read`, { targetKind, targetId })
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Lesen mit KI ließ sich nicht starten.'))
    }
  }

  const flaeche = darf ? ablageflaeche(setZiehen, (d) => void ablegen(d)) : {}
  const sichtbare = (zeilen ?? []).filter((z) => !z.isMailText)
  const texte = (zeilen ?? []).filter((z) => z.isMailText)

  return (
    <section className={`relative rounded-lg border bg-card p-4 shadow-sm ${ziehen ? 'border-primary ring-2 ring-primary/40' : 'border-border'}`} aria-label="Eingänge" {...flaeche}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Inbox className="h-4 w-4" aria-hidden /> Eingänge
        </h2>
        {darf && (
          <>
            <input ref={datei} type="file" multiple className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.xml,.eml,.msg" onChange={(e) => void ablegen(Array.from(e.target.files ?? []))} />
            <button type="button" disabled={laeuft} onClick={() => datei.current?.click()} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-60">
              {laeuft ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Ablegen
            </button>
          </>
        )}
      </div>
      {fehler && <div className="mb-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
      {hinweis && <div className="mb-2 rounded-md bg-muted px-3 py-2 text-sm">{hinweis}</div>}
      {zeilen && sichtbare.length === 0 && texte.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {darf ? 'Noch nichts eingegangen — Angebot, Bestätigung oder Lieferschein hierher ziehen.' : 'Noch nichts eingegangen.'}
        </p>
      )}
      {ziehen && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-primary/5 text-sm font-medium text-primary">
          <FolderInput className="mr-2 h-5 w-5" aria-hidden /> Loslassen legt ab und ordnet hier zu
        </div>
      )}
      <ul className="divide-y divide-border text-sm">
        {[...sichtbare, ...texte].map((z) => (
          <li key={z.assignmentId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5">
            <span className="min-w-[8rem] font-medium">{z.isMailText ? 'E-Mail' : z.kind ? ART_TEXT[z.kind] : 'Dokument'}</span>
            <button type="button" className="inline-flex min-w-0 max-w-[22rem] items-center gap-1 truncate text-left text-primary hover:underline" onClick={() => setVorschau(z)}>
              {z.isMailText ? <Mail className="h-3.5 w-3.5 flex-shrink-0" aria-hidden /> : <Eye className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />}
              <span className="truncate">{z.fileName}</span>
            </button>
            <span className="text-muted-foreground">{[z.senderName || z.senderAddress, fmtDateTime(z.receivedAt)].filter(Boolean).join(' · ')}</span>
            {mitKi && z.aiStatus === 'RUNNING' && (
              <KiArbeitet text="KI liest" className="text-xs text-muted-foreground" />
            )}
            {mitKi && z.aiStatus === 'FAILED' && (
              <span className="text-xs text-destructive" title={laufFehlerText(z.aiError) || undefined}>
                Lesen fehlgeschlagen
              </span>
            )}
            {/* rechts: „Mit KI lesen" (nur an Preisanfrage und Bestellung, nur mit `INBOX.AI`) und „Lösen" */}
            <span className="ml-auto inline-flex items-center gap-1">
              {mitKi && darf && !z.isMailText && !z.excludeFromAi && z.aiStatus !== 'RUNNING' && ANZEIGBAR.test(z.mimeType ?? '') && (
                <button type="button" onClick={() => void mitKiLesen(z)} className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground" title="Zeilen und Kopf lesen — sie stehen danach als Vorschlag unten">
                  <Sparkles className="h-3.5 w-3.5" /> {z.aiReadAt ? 'Erneut mit KI lesen' : 'Mit KI lesen'}
                </button>
              )}
              {darf && (
                <button type="button" onClick={() => void loesen(z)} className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground" title="Zuordnung lösen">
                  <Unlink className="h-3.5 w-3.5" /> Lösen
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {zeilen && zeilen.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Alle Einträge im{' '}
          <Link to="/inbox?status=ASSIGNED,DONE" className="text-primary hover:underline">
            Posteingang
          </Link>
        </p>
      )}
      {vorschau && <EingangVorschau eingang={vorschau} onClose={() => setVorschau(null)} />}
    </section>
  )
}

/** Vorschau eines Eingangs — PDF und Bild im Browser, sonst Herunterladen (15.: Office nie eingebettet). */
function EingangVorschau({ eingang, onClose }: { eingang: Eingang; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const zeigen = ANZEIGBAR.test(eingang.mimeType ?? '')
  useEffect(() => {
    if (!zeigen) return
    let u: string | null = null
    inhalt(eingang.itemId)
      .then((b) => {
        u = URL.createObjectURL(b)
        setUrl(u)
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Datei ließ sich nicht laden.')))
    return () => {
      if (u) URL.revokeObjectURL(u)
    }
  }, [eingang.itemId, zeigen])
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])
  const herunterladen = async () => {
    try {
      const b = await inhalt(eingang.itemId)
      downloadFile(await b.arrayBuffer(), eingang.fileName, b.type || eingang.mimeType || 'application/octet-stream')
    } catch (e) {
      setFehler(apiErrorText(e, 'Herunterladen hat nicht geklappt.'))
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={eingang.fileName} onClick={onClose}>
      <div className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-border shadow-xl" style={{ backgroundColor: 'hsl(var(--card))' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
          <span className="truncate font-medium">{eingang.fileName}</span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => void herunterladen()} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm hover:bg-muted">
              <Download className="h-4 w-4" /> Herunterladen
            </button>
            <button type="button" onClick={onClose} className="rounded-md p-1 hover:bg-muted" aria-label="Schließen">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center overflow-hidden bg-muted/40">
          {fehler && <p className="text-sm text-destructive">{fehler}</p>}
          {!fehler && !zeigen && <p className="px-6 text-center text-sm text-muted-foreground">Für diese Datei gibt es keine Vorschau — herunterladen und im passenden Programm öffnen.</p>}
          {!fehler && zeigen && !url && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
          {url && eingang.mimeType === 'application/pdf' && <iframe title={eingang.fileName} src={url} className="h-full w-full border-0" />}
          {url && eingang.mimeType?.startsWith('image/') && <img src={url} alt={eingang.fileName} className="max-h-full max-w-full object-contain" />}
        </div>
      </div>
    </div>
  )
}
